import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/tabon/Logo";
import { api, formatError } from "../lib/api";
import { Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(null); // null=loading, true, false
  const [transferInfo, setTransferInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError("No activation token found.");
      return;
    }
    // Validate token by checking account transfers
    api.get(`/auth/transfer/${token}`)
      .then(({ data }) => {
        setTransferInfo(data);
        setTokenValid(true);
      })
      .catch(() => {
        setTokenValid(false);
        setError("This invitation has expired or is invalid. Contact your TabOn administrator.");
      });
  }, [token]);

  const validatePassword = (p) => {
    if (p.length < 8) return "Password must be at least 8 characters.";
    if (!/\d/.test(p)) return "Password must contain at least one number.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const pwErr = validatePassword(newPassword);
    if (pwErr) { setError(pwErr); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/activate-account", { token, password: newPassword });
      setDone(true);
      toast.success("Account activated! Redirecting to login…");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 tb-hero-gradient">
      <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md tb-fade-up">
        <div className="flex flex-col items-center mb-8">
          <Logo size="xl" />
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-7 shadow-2xl">
          {tokenValid === null && (
            <div className="text-center py-8 text-[#A0A0A0] text-sm">Verifying invitation…</div>
          )}

          {tokenValid === false && (
            <div className="flex flex-col items-center gap-4 text-center py-4">
              <AlertCircle size={40} className="text-red-400" />
              <div>
                <h2 className="text-xl font-bold mb-1 text-red-400">Invalid Invitation</h2>
                <p className="text-sm text-[#A0A0A0]">{error}</p>
              </div>
            </div>
          )}

          {tokenValid === true && !done && (
            <>
              <div className="text-center mb-6">
                <div className="text-3xl mb-2">👋</div>
                <h1 className="text-2xl font-bold mb-1">Welcome to TabOn!</h1>
                {transferInfo?.venue_name && (
                  <p className="text-sm text-[#A0A0A0]">
                    <span className="text-white font-semibold">{transferInfo.venue_name}</span> has invited you to
                    manage their TabOn account.
                  </p>
                )}
                <p className="text-xs text-[#A0A0A0] mt-1">Set your password to get started.</p>
              </div>

              <form onSubmit={submit} className="flex flex-col gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      data-testid="activate-password"
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="Min 8 chars, include a number"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 pr-12 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                    />
                    <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                      {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      data-testid="activate-confirm"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Re-enter your password"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 pr-12 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                    />
                    <button type="button" onClick={() => setShowConfirm(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />{error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="activate-submit"
                  className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all"
                >
                  {loading ? "Activating…" : "Activate My Account"}
                </button>
              </form>
            </>
          )}

          {done && (
            <div className="flex flex-col items-center gap-4 text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle size={26} className="text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1">Account Activated!</h2>
                <p className="text-sm text-[#A0A0A0]">
                  Your account is ready. Redirecting to login…
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="text-center text-xs text-[#A0A0A0] mt-6">
          Powered by <span className="text-white">TabOn</span> · tabon.app
        </div>
      </div>
    </div>
  );
}
